import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { FiX, FiPlus, FiGift } = FiIcons

const CreateRewardModal = ({ isOpen, onClose }) => {
  const { createReward } = useFamily()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points_required: 50,
    emoji: '🎁'
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { data, error } = await createReward({
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        points_required: parseInt(formData.points_required),
        emoji: formData.emoji || '🎁'
      })

      if (!error && data) {
        setFormData({
          title: '',
          description: '',
          points_required: 50,
          emoji: '🎁'
        })
        onClose()
        toast.success('Reward created successfully! 🎉')
      }
    } catch (error) {
      console.error('Error creating reward:', error)
      toast.error('Failed to create reward')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const emojiOptions = [
    '🎁', '🏆', '⭐', '🎉', '🍦', '🎮', '📱', '🎬', '🍕', '🧸', 
    '🎈', '💎', '🌟', '🎪', '🎯', '🍭', '🎨', '⚽', '🎵', '🚗',
    '🏖️', '🎢', '🍪', '📚', '🎭', '🏀', '🎊', '🌈', '🦄', '🎂'
  ]

  const pointSuggestions = [
    { label: 'Quick Reward', points: 25, description: 'Small treats & privileges' },
    { label: 'Medium Reward', points: 50, description: 'Fun activities & outings' },
    { label: 'Big Reward', points: 100, description: 'Special experiences' },
    { label: 'Mega Reward', points: 200, description: 'Ultimate rewards' }
  ]

  const getPointRangeLabel = (points) => {
    if (points >= 150) return { label: 'Mega', color: 'text-purple-500' }
    if (points >= 80) return { label: 'Big', color: 'text-red-500' }
    if (points >= 40) return { label: 'Medium', color: 'text-orange-500' }
    return { label: 'Quick', color: 'text-green-500' }
  }

  const pointRange = getPointRangeLabel(formData.points_required)

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Reward</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                  disabled={loading}
                >
                  <SafeIcon icon={FiX} />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reward Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., Extra Screen Time"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Optional details about the reward..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Points Required *
                    </label>
                    <Badge variant="secondary" size="sm" className={pointRange.color}>
                      {pointRange.label} Reward
                    </Badge>
                  </div>
                  <input
                    type="number"
                    name="points_required"
                    value={formData.points_required}
                    onChange={handleChange}
                    min="1"
                    max="1000"
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  
                  {/* Point Suggestions */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {pointSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, points_required: suggestion.points }))}
                        className={`p-2 text-xs rounded-lg border transition-all duration-200 ${
                          formData.points_required === suggestion.points
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-600'
                        }`}
                      >
                        <div className="font-medium">{suggestion.label}</div>
                        <div className="text-xs opacity-75">{suggestion.points} pts</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Choose an Emoji
                  </label>
                  <div className="grid grid-cols-6 gap-2 border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {emojiOptions.map((emoji, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, emoji }))}
                        className={`p-2 rounded-lg text-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 ${
                          formData.emoji === emoji ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500' : ''
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview</h4>
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{formData.emoji}</div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {formData.title || 'Reward Title'}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formData.description || 'Reward description'}
                      </div>
                      <Badge variant="primary" size="sm" className="mt-1">
                        {formData.points_required} points
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1"
                    loading={loading}
                    disabled={!formData.title.trim() || !formData.points_required}
                  >
                    <SafeIcon icon={FiGift} className="text-sm" />
                    Create Reward
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default CreateRewardModal